import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import websocketService from '../services/websocketService';
import { 
  updateConnectionStatus, 
  updateUserStatus, 
  updateReconnectAttempts 
} from '../store/slices/websocketSlice';
import { addNotification } from '../store/slices/notificationSlice';
import { 
  updateFollowStatus 
} from '../store/slices/socialSlice';
import {
  commentCreated,
  commentUpdated,
  commentDeleted,
  commentLiked,
  commentUnliked,
} from '../store/slices/commentsSlice';
import { 
  addLike, 
  removeLike, 
  updateLikeStatus 
} from '../store/slices/likesSlice';
import { 
  addMessage,
  updateMessagesStatus,
  fetchUserChats,
} from '../store/slices/messagesSlice';
import { 
  addIncomingRequest,
  removeFriendRequest,
  addFriend,
  removeFriendFromList,
  updateFriendshipStatus
} from '../store/slices/friendsSlice';
import { 
  addGroup,
  removeGroup,
  addGroupMember,
  removeGroupMember,
  updateGroupInfo,
  updateMembershipStatus
} from '../store/slices/groupsSlice';
import { 
  addReport,
  updateReportStatus,
  blockContentAction,
  unblockContentAction,
  blockUserAction,
  unblockUserAction
} from '../store/slices/moderationSlice';

export const useWebSocket = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, token, user } = useSelector((state) => state.auth);
  const currentUserId = user?.id ?? null;
  const { isConnected, connectionStatus } = useSelector((state) => state.websocket);
  const fallbackPollingRef = useRef(null);
  
  // Используем ref для хранения обработчиков, чтобы избежать переподписки при их изменении
  // Инициализируем пустым объектом, так как обработчики еще не определены
  const handlersRef = useRef({});

  // Подключение к WebSocket при аутентификации
  useEffect(() => {
    if (isAuthenticated && token) {
      websocketService.connect(token);
    } else {
      websocketService.disconnect();
    }

    return () => {
      websocketService.disconnect();
    };
  }, [isAuthenticated, token]);

  // Обработчик уведомлений
  const handleNotification = useCallback((message) => {
    const data = message.data || message;
    const notificationType = data.type || message.type;
    const action = data.action;
    
    // Обрабатываем обновление статуса дружбы, если оно приходит как notification
    if (notificationType === 'friendship_status_updated' && data.status === 'rejected') {
      const requesterId = data.requester_id;
      const addresseeId = data.addressee_id;
      const friendshipId = data.friendship_id;
      const currentUserId = user?.id;
      
      // Если текущий пользователь - это requester (отправил запрос)
      if (requesterId === currentUserId) {
        // Обновляем статус для addressee_id, чтобы FriendButton на странице профиля requester обновился
        if (addresseeId) {
          dispatch(updateFriendshipStatus({ userId: addresseeId, status: 'none' }));
        }
        // Удаляем запрос из исходящих запросов по addressee_id
        dispatch(removeFriendRequest({ requestId: friendshipId, addresseeId: addresseeId }));
      }
      
      // Если текущий пользователь - это addressee (отклонил запрос)
      if (addresseeId === currentUserId) {
        // Обновляем статус для requester_id, чтобы FriendButton на странице профиля addressee обновился
        if (requesterId) {
          dispatch(updateFriendshipStatus({ userId: requesterId, status: 'none' }));
        }
        // Удаляем запрос из входящих запросов по requester_id
        dispatch(removeFriendRequest({ requesterId: requesterId, userId: addresseeId }));
      }
      
      // Не добавляем уведомление, так как это служебное событие
      return;
    }
    
    // Обрабатываем специальные типы уведомлений
    if (notificationType === 'friend_request') {
      // Преобразуем данные в формат, ожидаемый Redux
      const request = {
        id: data.id || data.friendship_id,
        requester_id: data.requester_id,
        addressee_id: data.addressee_id,
        status: data.status || 'pending',
        created_at: data.created_at || new Date().toISOString(),
        requester: data.requester || {
          id: data.requester_id,
          username: data.requester?.username || '',
          first_name: data.requester?.first_name || data.requester?.firstName || '',
          last_name: data.requester?.last_name || data.requester?.lastName || '',
          avatar_url: data.requester?.avatar_url || data.requester?.avatarUrl || null,
        }
      };
      
      dispatch(addIncomingRequest(request));
      
      // Добавляем уведомление (используем данные из WebSocket, которые уже содержат id)
      // Бэкенд отправляет полные данные уведомления, используем их
      if (data.id || data.friendship_id) {
        dispatch(addNotification({
          id: data.id || data.friendship_id,
          type: 'friend_request',
          title: data.title || 'Запрос в друзья',
          message: data.message || `${request.requester.first_name || request.requester.username} хочет добавить вас в друзья`,
          data: request,
          isRead: false,
          createdAt: data.created_at || request.created_at
        }));
      }
      return;
    }
    
    // Обрабатываем принятие запроса в друзья
    // Это событие обрабатывается в handleFriendAccepted, чтобы избежать дублирования
    if (notificationType === 'friend_accepted' || (action === 'custom' && data.friendship_id && (data.title?.includes('принят') || data.message?.includes('принял')))) {
      // Пропускаем обработку здесь, так как handleFriendAccepted уже обработает это событие
      return;
    }
    
    // Обрабатываем отклонение запроса в друзья (может приходить как notification с action: 'custom')
    if (notificationType === 'friend_rejected' || (action === 'custom' && data.friendship_id && (data.title?.includes('отклонен') || data.message?.includes('отклонил')))) {
      const friendshipId = data.friendship_id;
      const requesterId = data.requester_id; // Пользователь, отправивший запрос
      const addresseeId = data.addressee_id; // Пользователь, отклонивший запрос
      const currentUserId = user?.id;
      
      // Если уведомление пришло текущему пользователю, значит он отправил запрос (requester)
      // Удаляем запрос из исходящих запросов и обновляем статус
      if (requesterId && requesterId === currentUserId) {
        // Обновляем статус для addressee_id, чтобы FriendButton на странице профиля requester обновился
        if (addresseeId) {
          dispatch(updateFriendshipStatus({ userId: addresseeId, status: 'none' }));
        }
        // Удаляем запрос из исходящих запросов по addressee_id
        dispatch(removeFriendRequest({ requestId: friendshipId, addresseeId: addresseeId }));
      }
      
      // Если текущий пользователь - это addressee (отклонил запрос)
      // Удаляем запрос из входящих запросов и обновляем статус
      if (addresseeId === currentUserId && requesterId) {
        // Обновляем статус для requester_id, чтобы FriendButton на странице профиля addressee обновился
        dispatch(updateFriendshipStatus({ userId: requesterId, status: 'none' }));
        // Удаляем запрос из входящих запросов по requester_id
        dispatch(removeFriendRequest({ requesterId: requesterId, userId: addresseeId }));
      }
      
      // Добавляем уведомление только для requester (пользователя, отправившего запрос)
      if (requesterId === currentUserId) {
        dispatch(addNotification({
          type: 'friend_rejected',
          title: data.title || 'Запрос в друзья отклонен',
          message: data.message || 'Ваш запрос в друзья был отклонен',
          data: data,
          isRead: false,
          createdAt: data.created_at || new Date().toISOString()
        }));
      }
      return;
    }
    
    // Для остальных типов просто добавляем уведомление
    // Но пропускаем friend_accepted, так как оно уже обработано в handleFriendAccepted
    const finalData = message.data || message;
    const finalType = finalData.type || notificationType;
    
    // Обрабатываем уведомления об ответах на обращения
    if (finalType === 'contact_reply' || notificationType === 'contact_reply') {
      const contactMessageId = finalData.contact_message_id || finalData.data?.contact_message_id;
      // Отправляем событие для обновления списка обращений
      if (contactMessageId) {
        window.dispatchEvent(new CustomEvent('contact_reply_received', {
          detail: { contactMessageId }
        }));
      }
      // Добавляем уведомление
      dispatch(addNotification(finalData));
      return;
    }
    
    if (finalType !== 'friend_accepted') {
      dispatch(addNotification(finalData));
    }
  }, [dispatch, user]);

  // Обработчик новых комментариев (старый, для совместимости)
  // Теперь используем новую систему комментариев через WebSocket события
  const handleNewComment = useCallback((message) => {
    // Игнорируем, так как используем новую систему через COMMENT_CREATED
  }, [dispatch]);

  // Обработчики новой системы комментариев
  const handleCommentCreated = useCallback((message) => {
    // message имеет структуру { type: 'COMMENT_CREATED', data: { comment: {...}, content_type: '...', content_id: ... } }
    const data = message.data || message;
    if (data.comment) {
      dispatch(commentCreated({ comment: data.comment }));
    }
  }, [dispatch]);

  const handleCommentUpdated = useCallback((message) => {
    const data = message.data || message;
    if (data.comment) {
      dispatch(commentUpdated({ comment: data.comment }));
    }
  }, [dispatch]);

  const handleCommentDeleted = useCallback((message) => {
    const data = message.data || message;
    // Проверяем разные варианты структуры данных
    if (data.comment_id && data.content_type && data.content_id) {
      dispatch(commentDeleted({
        commentId: data.comment_id,
        contentType: data.content_type,
        contentId: data.content_id,
      }));
    } else if (data.comment && data.comment.id) {
      // Если пришел полный комментарий, извлекаем данные из него
      dispatch(commentDeleted({
        commentId: data.comment.id,
        contentType: data.comment.content_type,
        contentId: data.comment.content_id,
      }));
    }
  }, [dispatch]);

  const handleCommentLiked = useCallback((message) => {
    const data = message.data || message;
    if (data.comment) {
      dispatch(commentLiked({ comment: data.comment }));
    }
  }, [dispatch]);

  const handleCommentUnliked = useCallback((message) => {
    const data = message.data || message;
    if (data.comment) {
      dispatch(commentUnliked({ comment: data.comment }));
    }
  }, [dispatch]);

  // Обработчик новых лайков
  const handleNewLike = useCallback((message) => {
    const { contentType, contentId, user, count } = message.data;
    
    // Добавляем лайк
    dispatch(addLike({
      contentType,
      contentId,
      user
    }));
    
    // Обновляем статус лайка
    dispatch(updateLikeStatus({
      contentType,
      contentId,
      isLiked: false, // Для текущего пользователя
      count
    }));
    
    // Добавляем уведомление
    dispatch(addNotification({
      type: 'like',
      title: 'Новый лайк',
      message: `${user.firstName} ${user.lastName} поставил лайк вашему контенту`,
      data: message.data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch]);

  // Обработчик новых подписок
  const handleNewFollow = useCallback((message) => {
    const { follower_id, following_id } = message.data;
    
    // Обновляем статус подписки
    dispatch(updateFollowStatus({ 
      userId: following_id, 
      isFollowing: true 
    }));
    
    // Добавляем уведомление
    dispatch(addNotification({
      type: 'follow',
      title: 'Новая подписка',
      message: `${message.data.follower_username} подписался на вас`,
      data: message.data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch]);

  // Обработчик новых сообщений
  const handleNewMessage = useCallback((message) => {
    if (!message || !message.data) {
      return;
    }

    const payload = message.data;
    const chatId = payload.chatId || payload.chat_id;
    if (!chatId) {
      console.warn('Received WS message without chat id', message);
      return;
    }


    dispatch(addMessage({ message: payload, currentUserId }));

    const senderFirst = payload.user?.firstName || payload.user?.first_name || 'Пользователь';
    const senderLast = payload.user?.lastName || payload.user?.last_name || '';
    const preview = payload.content || payload.text || '';

    dispatch(addNotification({
      type: 'message',
      title: 'Новое сообщение',
      message: `${senderFirst} ${senderLast}: ${preview}`.trim(),
      data: payload,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch, currentUserId]);

  // Обработчик прочтения сообщений
  const handleMessagesRead = useCallback((message) => {
    const payload = message.data;
    const chatId = payload.chatId || payload.chat_id;
    const messageIds = payload.messageIds || [];
    
    
    // Обновляем статус сообщений в Redux
    dispatch(updateMessagesStatus({ chatId, messageIds, isRead: true }));
  }, [dispatch]);

  // Обработчик запросов в друзья
  const handleFriendRequest = useCallback((message) => {
    const data = message.data || message;
    
    // Преобразуем данные в формат, ожидаемый Redux
    const request = {
      id: data.id || data.friendship_id,
      requester_id: data.requester_id,
      addressee_id: data.addressee_id,
      status: data.status || 'pending',
      created_at: data.created_at || new Date().toISOString(),
      requester: data.requester || {
        id: data.requester_id,
        username: data.requester?.username || '',
        first_name: data.requester?.first_name || data.requester?.firstName || '',
        last_name: data.requester?.last_name || data.requester?.lastName || '',
        avatar_url: data.requester?.avatar_url || data.requester?.avatarUrl || null,
      }
    };
    
    dispatch(addIncomingRequest(request));
    
    // Добавляем уведомление (используем данные из WebSocket, которые уже содержат id)
    // Бэкенд отправляет полные данные уведомления, используем их
    const notificationId = data.id || data.friendship_id;
    if (notificationId) {
      dispatch(addNotification({
        id: notificationId,
        type: 'friend_request',
        title: data.title || 'Запрос в друзья',
        message: data.message || `${request.requester.first_name || request.requester.username} хочет добавить вас в друзья`,
        data: request,
        isRead: false,
        createdAt: data.created_at || request.created_at
      }));
    }
  }, [dispatch]);

  // Обработчик принятия/отклонения запроса в друзья
  const handleFriendRequestResponse = useCallback((message) => {
    const { requestId, userId, accepted } = message.data;
    
    if (accepted) {
      dispatch(addFriend({ userId, friend: message.data.friend }));
      dispatch(addNotification({
        type: 'friend_accepted',
        title: 'Запрос принят',
        message: `${message.data.friend.firstName} ${message.data.friend.lastName} принял(а) ваш запрос в друзья`,
        data: message.data,
        isRead: false,
        createdAt: new Date().toISOString()
      }));
    } else {
      dispatch(removeFriendRequest({ requestId, userId }));
    }
  }, [dispatch]);

  // Обработчик принятия запроса в друзья (для обоих пользователей)
  const handleFriendAccepted = useCallback((message) => {
    const data = message.data || message;
    const currentUserId = user?.id;
    
    // Если это уведомление для текущего пользователя (который принял запрос - addressee)
    if (data.addressee_id === currentUserId && data.friend) {
      const friend = data.friend;
      const requesterId = data.requester_id || data.friend_id;
      
      // Добавляем друга в список друзей текущего пользователя
      dispatch(addFriend({ 
        userId: currentUserId, 
        friend: {
          id: friend.id || requesterId,
          first_name: friend.first_name || friend.firstName,
          last_name: friend.last_name || friend.lastName,
          username: friend.username,
          avatar_url: friend.avatar_url || friend.avatarUrl,
        }
      }));
      
      // Обновляем статус дружбы
      if (requesterId) {
        dispatch(updateFriendshipStatus({ userId: requesterId, status: 'friends' }));
      }
    }
    
    // Если это уведомление для пользователя, который отправил запрос (requester)
    if (data.requester_id === currentUserId && data.friend) {
      const friend = data.friend;
      const addresseeId = data.addressee_id || data.friend_id;
      
      // Добавляем друга в список друзей текущего пользователя (requester)
      dispatch(addFriend({ 
        userId: currentUserId, 
        friend: {
          id: friend.id || addresseeId,
          first_name: friend.first_name || friend.firstName,
          last_name: friend.last_name || friend.lastName,
          username: friend.username,
          avatar_url: friend.avatar_url || friend.avatarUrl,
        }
      }));
      
      // Обновляем статус дружбы
      if (addresseeId) {
        dispatch(updateFriendshipStatus({ userId: addresseeId, status: 'friends' }));
      }
      
      // Удаляем запрос из исходящих запросов
      if (addresseeId) {
        dispatch(removeFriendRequest({ addresseeId: addresseeId }));
      }
      
      // Добавляем уведомление
      dispatch(addNotification({
        type: 'friend_accepted',
        title: data.title || 'Запрос в друзья принят',
        message: data.message || 'Ваш запрос в друзья был принят',
        data: data,
        isRead: false,
        createdAt: data.created_at || new Date().toISOString()
      }));
    }
  }, [dispatch, user]);

  // Обработчик отклонения запроса в друзья
  const handleFriendRejected = useCallback((message) => {
    const data = message.data || message;
    const requesterId = data.requester_id; // Пользователь, отправивший запрос
    const addresseeId = data.addressee_id; // Пользователь, отклонивший запрос
    const friendshipId = data.friendship_id;
    const currentUserId = user?.id;
    
    // Если текущий пользователь - это requester (отправил запрос), который получил уведомление об отклонении
    if (requesterId === currentUserId) {
      // Обновляем статус для addressee_id, чтобы FriendButton на странице профиля requester обновился
      if (addresseeId) {
        dispatch(updateFriendshipStatus({ userId: addresseeId, status: 'none' }));
      }
      // Удаляем запрос из исходящих запросов
      // Для исходящих запросов нужно удалять по addressee_id, а не по requester_id
      dispatch(removeFriendRequest({ requestId: friendshipId, addresseeId: addresseeId }));
    }
    
    // Если текущий пользователь - это addressee (отклонил запрос)
    if (addresseeId === currentUserId) {
      // Обновляем статус для requester_id, чтобы FriendButton на странице профиля addressee обновился
      if (requesterId) {
        dispatch(updateFriendshipStatus({ userId: requesterId, status: 'none' }));
      }
      // Удаляем запрос из входящих запросов
      dispatch(removeFriendRequest({ requesterId: requesterId, userId: addresseeId }));
    }
    
    dispatch(addNotification({
      type: 'friend_rejected',
      title: data.title || 'Запрос в друзья отклонен',
      message: data.message || 'Ваш запрос в друзья был отклонен',
      data: data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch, user]);

  // Обработчик обновления статуса дружбы
  const handleFriendshipStatusUpdated = useCallback((message) => {
    const data = message.data || message;
    const currentUserId = user?.id;
    const requesterId = data.requester_id;
    const addresseeId = data.addressee_id;
    const friendshipId = data.friendship_id;
    const friendId = data.friend_id;
    
    if (data.status === 'rejected') {
      // Если текущий пользователь - это requester (отправил запрос)
      if (requesterId === currentUserId) {
        // Обновляем статус для addressee_id, чтобы FriendButton на странице профиля requester обновился
        if (addresseeId) {
          dispatch(updateFriendshipStatus({ userId: addresseeId, status: 'none' }));
        }
        // Удаляем запрос из исходящих запросов
        // Для исходящих запросов нужно удалять по addressee_id, а не по requester_id
        dispatch(removeFriendRequest({ requestId: friendshipId, addresseeId: addresseeId }));
      }
      
      // Если текущий пользователь - это addressee (отклонил запрос)
      if (addresseeId === currentUserId) {
        // Обновляем статус для requester_id, чтобы FriendButton на странице профиля addressee обновился
        if (requesterId) {
          dispatch(updateFriendshipStatus({ userId: requesterId, status: 'none' }));
        }
        // Удаляем запрос из входящих запросов
        dispatch(removeFriendRequest({ requesterId: requesterId, userId: addresseeId }));
      }
    } else if (data.status === 'accepted') {
      // Если текущий пользователь - это requester (отправил запрос)
      if (requesterId === currentUserId) {
        // Обновляем статус для addressee_id
        if (addresseeId) {
          dispatch(updateFriendshipStatus({ userId: addresseeId, status: 'friends' }));
        }
        // Удаляем запрос из исходящих запросов
        dispatch(removeFriendRequest({ requestId: friendshipId, addresseeId: addresseeId }));
      }
      
      // Если текущий пользователь - это addressee (принял запрос)
      if (addresseeId === currentUserId) {
        // Обновляем статус для requester_id
        if (requesterId) {
          dispatch(updateFriendshipStatus({ userId: requesterId, status: 'friends' }));
        }
        // Удаляем запрос из входящих запросов
        dispatch(removeFriendRequest({ requesterId: requesterId, userId: addresseeId }));
      }
    } else if (data.status === 'cancelled') {
      // Если текущий пользователь - это requester (отменил запрос)
      if (requesterId === currentUserId) {
        // Обновляем статус для addressee_id
        if (addresseeId) {
          dispatch(updateFriendshipStatus({ userId: addresseeId, status: 'none' }));
        }
        // Удаляем запрос из исходящих запросов
        dispatch(removeFriendRequest({ requestId: friendshipId, addresseeId: addresseeId }));
      }
      
      // Если текущий пользователь - это addressee (кому отправили запрос)
      if (addresseeId === currentUserId) {
        // Обновляем статус для requester_id
        if (requesterId) {
          dispatch(updateFriendshipStatus({ userId: requesterId, status: 'none' }));
        }
        // Удаляем запрос из входящих запросов (если он там был)
        dispatch(removeFriendRequest({ requesterId: requesterId, userId: addresseeId }));
      }
    } else if (data.status === 'none' && friendId) {
      // Обновление статуса при удалении друга
      if (friendId === currentUserId || (requesterId === currentUserId && addresseeId === friendId) || (addresseeId === currentUserId && requesterId === friendId)) {
        // Обновляем статус для обоих пользователей
        if (friendId) {
          dispatch(updateFriendshipStatus({ userId: friendId, status: 'none' }));
        }
        if (requesterId && requesterId !== friendId) {
          dispatch(updateFriendshipStatus({ userId: requesterId, status: 'none' }));
        }
        if (addresseeId && addresseeId !== friendId) {
          dispatch(updateFriendshipStatus({ userId: addresseeId, status: 'none' }));
        }
      }
    }
  }, [dispatch, user]);

  // Обработчик удаления из друзей
  const handleFriendRemoved = useCallback((message) => {
    const data = message.data || message;
    const userId = data.user_id || data.userId;
    const friendId = data.friend_id || data.friendId;
    const currentUserId = user?.id;
    
    // Удаляем друга из списка друзей для обоих пользователей
    if (userId === currentUserId) {
      // Текущий пользователь удалил друга
      dispatch(removeFriendFromList({ userId: currentUserId, friendId }));
      // Обновляем статус дружбы
      if (friendId) {
        dispatch(updateFriendshipStatus({ userId: friendId, status: 'none' }));
      }
    } else if (friendId === currentUserId) {
      // Текущего пользователя удалили из друзей
      dispatch(removeFriendFromList({ userId: userId, friendId: currentUserId }));
      // Обновляем статус дружбы
      if (userId) {
        dispatch(updateFriendshipStatus({ userId: userId, status: 'none' }));
      }
      // Добавляем уведомление только для того, кого удалили
      if (data.friend) {
        dispatch(addNotification({
          type: 'friend_removed',
          title: 'Удален из друзей',
          message: `${data.friend.first_name || data.friend.firstName} ${data.friend.last_name || data.friend.lastName} удалил(а) вас из друзей`,
          data: data,
          isRead: false,
          createdAt: new Date().toISOString()
        }));
      }
    }
  }, [dispatch, user]);

  // Обработчик создания группы
  const handleGroupCreated = useCallback((message) => {
    dispatch(addGroup(message.data));
    
    // Добавляем уведомление
    dispatch(addNotification({
      type: 'group_created',
      title: 'Группа создана',
      message: `Группа "${message.data.name}" успешно создана`,
      data: message.data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch]);

  // Обработчик присоединения к группе
  const handleGroupJoined = useCallback((message) => {
    const { groupId, member } = message.data;
    
    dispatch(addGroupMember({ groupId, member }));
    dispatch(updateMembershipStatus({ groupId, status: 'member' }));
    
    // Добавляем уведомление
    dispatch(addNotification({
      type: 'group_joined',
      title: 'Присоединились к группе',
      message: `Вы присоединились к группе "${message.data.groupName}"`,
      data: message.data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch]);

  // Обработчик выхода из группы
  const handleGroupLeft = useCallback((message) => {
    const { groupId, userId } = message.data;
    
    dispatch(removeGroupMember({ groupId, userId }));
    dispatch(updateMembershipStatus({ groupId, status: 'none' }));
  }, [dispatch]);

  // Обработчик обновления группы
  const handleGroupUpdated = useCallback((message) => {
    dispatch(updateGroupInfo(message.data));
  }, [dispatch]);

  // Обработчик удаления группы
  const handleGroupDeleted = useCallback((message) => {
    dispatch(removeGroup(message.data.groupId));
    
    // Добавляем уведомление
    dispatch(addNotification({
      type: 'group_deleted',
      title: 'Группа удалена',
      message: `Группа "${message.data.groupName}" была удалена`,
      data: message.data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch]);

  // Обработчик новых жалоб
  const handleNewReport = useCallback((message) => {
    dispatch(addReport(message.data));
    
    // Добавляем уведомление для модераторов
    dispatch(addNotification({
      type: 'new_report',
      title: 'Новая жалоба',
      message: `Получена жалоба на ${message.data.contentType} (ID: ${message.data.contentId})`,
      data: message.data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch]);

  // Обработчик обновления статуса жалобы
  const handleReportStatusUpdate = useCallback((message) => {
    dispatch(updateReportStatus(message.data));
  }, [dispatch]);

  // Обработчик блокировки контента
  const handleContentBlocked = useCallback((message) => {
    dispatch(blockContentAction(message.data));
    
    // Добавляем уведомление
    dispatch(addNotification({
      type: 'content_blocked',
      title: 'Контент заблокирован',
      message: `${message.data.contentType} (ID: ${message.data.contentId}) был заблокирован`,
      data: message.data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch]);

  // Обработчик разблокировки контента
  const handleContentUnblocked = useCallback((message) => {
    dispatch(unblockContentAction(message.data));
  }, [dispatch]);

  // Обработчик блокировки пользователя
  const handleUserBlocked = useCallback((message) => {
    dispatch(blockUserAction(message.data));
    
    // Добавляем уведомление
    dispatch(addNotification({
      type: 'user_blocked',
      title: 'Пользователь заблокирован',
      message: `Пользователь ${message.data.userName} был заблокирован`,
      data: message.data,
      isRead: false,
      createdAt: new Date().toISOString()
    }));
  }, [dispatch]);

  // Обработчик разблокировки пользователя
  const handleUserUnblocked = useCallback((message) => {
    dispatch(unblockUserAction(message.data));
  }, [dispatch]);

  // Обработчик статуса пользователей
  const handleUserStatus = useCallback((message) => {
    const { user_id, username } = message.data;
    const status = message.type === 'user_online' ? 'online' : 'offline';
    
    dispatch(updateUserStatus({
      userId: user_id,
      status,
      username
    }));
  }, [dispatch]);

  // Обработчик ошибок
  const handleError = useCallback((error) => {
    console.error('WebSocket error:', error);
    dispatch(updateConnectionStatus({ 
      status: 'error', 
      error: error.error || 'WebSocket connection error' 
    }));
  }, [dispatch]);

  // Обработчик статуса подключения
  const handleConnectionStatus = useCallback((data) => {
    dispatch(updateConnectionStatus(data));
  }, [dispatch]);

  // Обновляем ref при изменении обработчиков
  useEffect(() => {
    handlersRef.current = {
      handleNotification,
      handleNewComment,
      handleNewLike,
      handleNewFollow,
      handleNewMessage,
      handleMessagesRead,
      handleFriendRequest,
      handleFriendRequestResponse,
      handleFriendAccepted,
      handleFriendRejected,
      handleFriendshipStatusUpdated,
      handleFriendRemoved,
      handleGroupCreated,
      handleGroupJoined,
      handleGroupLeft,
      handleGroupUpdated,
      handleGroupDeleted,
      handleNewReport,
      handleReportStatusUpdate,
      handleContentBlocked,
      handleContentUnblocked,
      handleUserBlocked,
      handleUserUnblocked,
      handleUserStatus,
      handleError,
      handleConnectionStatus,
      handleCommentCreated,
      handleCommentUpdated,
      handleCommentDeleted,
      handleCommentLiked,
      handleCommentUnliked,
    };
  }, [
    handleNotification,
    handleNewComment,
    handleNewLike,
    handleNewFollow,
    handleNewMessage,
    handleMessagesRead,
    handleFriendRequest,
    handleFriendRequestResponse,
    handleFriendAccepted,
    handleFriendRejected,
    handleFriendshipStatusUpdated,
    handleFriendRemoved,
    handleGroupCreated,
    handleGroupJoined,
    handleGroupLeft,
    handleGroupUpdated,
    handleGroupDeleted,
    handleNewReport,
    handleReportStatusUpdate,
    handleContentBlocked,
    handleContentUnblocked,
    handleUserBlocked,
    handleUserUnblocked,
    handleUserStatus,
    handleError,
    handleConnectionStatus,
    handleCommentCreated,
    handleCommentUpdated,
    handleCommentDeleted,
    handleCommentLiked,
    handleCommentUnliked,
  ]);

  // Подписка на события WebSocket
  useEffect(() => {
    if (!isAuthenticated) return;

    // Используем обертки, которые всегда вызывают актуальные обработчики из ref
    const wrapHandler = (handlerName) => (data) => {
      const handler = handlersRef.current[handlerName];
      if (handler) {
        handler(data);
      }
    };

    // Подписываемся на события
    const unsubscribeNotification = websocketService.subscribe('notification', wrapHandler('handleNotification'));
    const unsubscribeNewComment = websocketService.subscribe('new_comment', wrapHandler('handleNewComment'));
    const unsubscribeNewLike = websocketService.subscribe('new_like', wrapHandler('handleNewLike'));
    const unsubscribeNewFollow = websocketService.subscribe('new_follow', wrapHandler('handleNewFollow'));
    const unsubscribeNewMessage = websocketService.subscribe('new_message', wrapHandler('handleNewMessage'));
    const unsubscribeMessagesRead = websocketService.subscribe('messages_read', wrapHandler('handleMessagesRead'));
    const unsubscribeFriendRequest = websocketService.subscribe('friend_request', wrapHandler('handleFriendRequest'));
    const unsubscribeFriendRequestResponse = websocketService.subscribe('friend_request_response', wrapHandler('handleFriendRequestResponse'));
    const unsubscribeFriendAccepted = websocketService.subscribe('friend_accepted', wrapHandler('handleFriendAccepted'));
    const unsubscribeFriendRejected = websocketService.subscribe('friend_rejected', wrapHandler('handleFriendRejected'));
    const unsubscribeFriendshipStatusUpdated = websocketService.subscribe('friendship_status_updated', wrapHandler('handleFriendshipStatusUpdated'));
    const unsubscribeFriendRemoved = websocketService.subscribe('friend_removed', wrapHandler('handleFriendRemoved'));
    const unsubscribeGroupCreated = websocketService.subscribe('group_created', wrapHandler('handleGroupCreated'));
    const unsubscribeGroupJoined = websocketService.subscribe('group_joined', wrapHandler('handleGroupJoined'));
    const unsubscribeGroupLeft = websocketService.subscribe('group_left', wrapHandler('handleGroupLeft'));
    const unsubscribeGroupUpdated = websocketService.subscribe('group_updated', wrapHandler('handleGroupUpdated'));
    const unsubscribeGroupDeleted = websocketService.subscribe('group_deleted', wrapHandler('handleGroupDeleted'));
    const unsubscribeNewReport = websocketService.subscribe('new_report', wrapHandler('handleNewReport'));
    const unsubscribeReportStatusUpdate = websocketService.subscribe('report_status_update', wrapHandler('handleReportStatusUpdate'));
    const unsubscribeContentBlocked = websocketService.subscribe('content_blocked', wrapHandler('handleContentBlocked'));
    const unsubscribeContentUnblocked = websocketService.subscribe('content_unblocked', wrapHandler('handleContentUnblocked'));
    const unsubscribeUserBlocked = websocketService.subscribe('user_blocked', wrapHandler('handleUserBlocked'));
    const unsubscribeUserUnblocked = websocketService.subscribe('user_unblocked', wrapHandler('handleUserUnblocked'));
    const unsubscribeUserOnline = websocketService.subscribe('user_online', wrapHandler('handleUserStatus'));
    const unsubscribeUserOffline = websocketService.subscribe('user_offline', wrapHandler('handleUserStatus'));
    const unsubscribeError = websocketService.subscribe('error', wrapHandler('handleError'));
    const unsubscribeConnection = websocketService.subscribe('connection', wrapHandler('handleConnectionStatus'));
    
    // Подписка на события комментариев
    const unsubscribeCommentCreated = websocketService.subscribe('COMMENT_CREATED', wrapHandler('handleCommentCreated'));
    const unsubscribeCommentUpdated = websocketService.subscribe('COMMENT_UPDATED', wrapHandler('handleCommentUpdated'));
    const unsubscribeCommentDeleted = websocketService.subscribe('COMMENT_DELETED', wrapHandler('handleCommentDeleted'));
    const unsubscribeCommentLiked = websocketService.subscribe('COMMENT_LIKED', wrapHandler('handleCommentLiked'));
    const unsubscribeCommentUnliked = websocketService.subscribe('COMMENT_UNLIKED', wrapHandler('handleCommentUnliked'));

    // Отписка при размонтировании
    return () => {
      unsubscribeNotification();
      unsubscribeNewComment();
      unsubscribeNewLike();
      unsubscribeNewFollow();
      unsubscribeNewMessage();
      unsubscribeMessagesRead();
      unsubscribeFriendRequest();
      unsubscribeFriendRequestResponse();
      unsubscribeFriendAccepted();
      unsubscribeFriendRejected();
      unsubscribeFriendshipStatusUpdated();
      unsubscribeFriendRemoved();
      unsubscribeGroupCreated();
      unsubscribeGroupJoined();
      unsubscribeGroupLeft();
      unsubscribeGroupUpdated();
      unsubscribeGroupDeleted();
      unsubscribeNewReport();
      unsubscribeReportStatusUpdate();
      unsubscribeContentBlocked();
      unsubscribeContentUnblocked();
      unsubscribeUserBlocked();
      unsubscribeUserUnblocked();
      unsubscribeUserOnline();
      unsubscribeUserOffline();
      unsubscribeError();
      unsubscribeConnection();
      unsubscribeCommentCreated();
      unsubscribeCommentUpdated();
      unsubscribeCommentDeleted();
      unsubscribeCommentLiked();
      unsubscribeCommentUnliked();
    };
  }, [isAuthenticated]);

  // Отслеживание попыток переподключения
  useEffect(() => {
    const interval = setInterval(() => {
      const status = websocketService.getConnectionStatus();
      if (status.reconnectAttempts !== undefined) {
        dispatch(updateReconnectAttempts(status.reconnectAttempts));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Функция для отправки сообщений
  const sendMessage = useCallback((message) => {
    websocketService.sendMessage(message);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (fallbackPollingRef.current) {
        clearInterval(fallbackPollingRef.current);
        fallbackPollingRef.current = null;
      }
      return;
    }

    if (!isConnected) {
      if (!fallbackPollingRef.current) {
        dispatch(fetchUserChats());
        fallbackPollingRef.current = setInterval(() => {
          dispatch(fetchUserChats());
        }, 15000);
      }
    } else if (fallbackPollingRef.current) {
      clearInterval(fallbackPollingRef.current);
      fallbackPollingRef.current = null;
    }

    return () => {
      if (fallbackPollingRef.current) {
        clearInterval(fallbackPollingRef.current);
        fallbackPollingRef.current = null;
      }
    };
  }, [dispatch, isAuthenticated, isConnected]);

  return {
    isConnected,
    connectionStatus,
    sendMessage,
    reconnect: () => {
      if (token) {
        websocketService.connect(token);
      }
    }
  };
};
